import {ProgressBar, Link} from '@primer/react'
import {GitPullRequestIcon, GitMergeIcon, IssueOpenedIcon, IssueClosedIcon} from '@primer/octicons-react'
import type {PulseOverviewDataPoint} from '../types'
import {useParams} from '@github-ui/react-router'

// Function to calculate the ratio for progress bars

function getRatio(type: string, overviewData: PulseOverviewDataPoint): number {
  switch (type) {
    case 'pulls_merged_ratio':
      return overviewData.totalPullRequests === 0 ? 0 : overviewData.mergedPulls.length / overviewData.totalPullRequests
    case 'pulls_opened_ratio':
      return overviewData.totalPullRequests === 0 ? 0 : overviewData.newPulls.length / overviewData.totalPullRequests
    case 'issues_opened_ratio':
      return overviewData.totalIssues === 0 ? 0 : overviewData.newIssues.length / overviewData.totalIssues
    case 'issues_closed_ratio':
      return overviewData.totalIssues === 0 ? 0 : overviewData.closedIssues.length / overviewData.totalIssues
    default:
      return 0
  }
}

export function PulseOverview({overviewData}: {overviewData: PulseOverviewDataPoint}) {
  const {owner, repo} = useParams()
  const repoPath = `/${owner}/${repo}`

  return (
    <div className="Box">
      <div className="Box-header">
        <h3 className="Box-title" data-hpc>
          Overview
        </h3>
      </div>

      <ul>
        <li className="Box-row p-0 d-flex flex-wrap">
          {/* Pull Requests Progress */}
          <div className="col-12 col-md-6 pr-px-3 pr-py-4">
            <div className="position-relative">
              <ProgressBar>
                <ProgressBar.Item
                  progress={getRatio('pulls_merged_ratio', overviewData) * 100}
                  aria-label="Merged Pull Requests"
                  style={{backgroundColor: 'var(--bgColor-done-emphasis)'}}
                />
                <ProgressBar.Item
                  progress={getRatio('pulls_opened_ratio', overviewData) * 100}
                  aria-label="Open Pull Requests"
                  style={{backgroundColor: 'var(--bgColor-success-emphasis)'}}
                />
              </ProgressBar>

              {/* Clickable overlay for merged PRs */}
              <Link
                href={`${repoPath}/pulls?q=is%3Apr+is%3Aclosed`}
                className="position-absolute"
                style={{
                  left: 0,
                  top: 0,
                  width: `${getRatio('pulls_merged_ratio', overviewData) * 100}%`,
                  height: '100%',
                  zIndex: 1,
                }}
                aria-label="View merged pull requests"
              />

              {/* Clickable overlay for open PRs */}
              <Link
                href={`${repoPath}/pulls?q=is%3Apr+is%3Aopen`}
                className="position-absolute"
                style={{
                  left: `${getRatio('pulls_merged_ratio', overviewData) * 100}%`,
                  top: 0,
                  width: `${getRatio('pulls_opened_ratio', overviewData) * 100}%`,
                  height: '100%',
                  zIndex: 1,
                }}
                aria-label="View open pull requests"
              />
            </div>

            <div className="mt-2">
              <span className="text-emphasized">{overviewData.newPulls.length + overviewData.mergedPulls.length}</span>{' '}
              Active pull requests
            </div>
          </div>

          {/* Issues Progress */}
          <div className="col-12 col-md-6 pr-px-3 pr-py-4">
            <div className="position-relative">
              <ProgressBar>
                <ProgressBar.Item
                  progress={getRatio('issues_closed_ratio', overviewData) * 100}
                  aria-label="Closed Issues"
                  style={{backgroundColor: 'var(--bgColor-danger-emphasis)'}}
                />
                <ProgressBar.Item
                  progress={getRatio('issues_opened_ratio', overviewData) * 100}
                  aria-label="Active Issues"
                  style={{backgroundColor: 'var(--bgColor-success-emphasis)'}}
                />
              </ProgressBar>

              {/* Clickable overlay for closed issues */}
              <Link
                href={`${repoPath}/issues?q=is%3Aissue+is%3Aclosed`}
                className="position-absolute"
                style={{
                  left: 0,
                  top: 0,
                  width: `${getRatio('issues_closed_ratio', overviewData) * 100}%`,
                  height: '100%',
                  zIndex: 1,
                }}
                aria-label="View closed issues"
              />

              {/* Clickable overlay for open issues */}
              <Link
                href={`${repoPath}/issues?q=is%3Aissue+is%3Aopen`}
                className="position-absolute"
                style={{
                  left: `${getRatio('issues_closed_ratio', overviewData) * 100}%`,
                  top: 0,
                  width: `${getRatio('issues_opened_ratio', overviewData) * 100}%`,
                  height: '100%',
                  zIndex: 1,
                }}
                aria-label="View open issues"
              />
            </div>

            <div className="mt-2">
              <span className="text-emphasized">
                {overviewData.newIssues.length + overviewData.closedIssues.length}
              </span>{' '}
              Active issues
            </div>
          </div>
        </li>

        <li className="Box-row p-0">
          <ul className="list-style-none text-center d-flex flex-wrap">
            {/* Merged Pull Requests */}
            <li className="pr-p-3 col-12 col-sm-6 col-md-3 border-bottom border-sm-right border-md-bottom-0 color-border-muted">
              <Link
                href="#merged-pull-requests"
                className="d-block color-fg-default no-underline Link--muted Link--inTextBlock"
              >
                <span className="d-block h4 color-fg-default">
                  <GitMergeIcon className="color-fg-done" /> {overviewData.mergedPulls.length}
                </span>
                <span className="color-fg-muted">Merged pull requests</span>
              </Link>
            </li>

            {/* Open Pull Requests */}
            <li className="pr-p-3 col-12 col-sm-6 col-md-3 border-bottom border-md-bottom-0 border-md-right color-border-muted">
              <Link
                href="#opened-pull-requests"
                className="d-block color-fg-default no-underline Link--muted Link--inTextBlock"
              >
                <span className="d-block h4 color-fg-default">
                  <GitPullRequestIcon className="color-fg-success" /> {overviewData.newPulls.length}
                </span>
                <span className="color-fg-muted">Open pull requests</span>
              </Link>
            </li>

            {/* Closed Issues */}
            <li className="pr-p-3 col-12 col-sm-6 col-md-3 border-bottom border-sm-bottom-0 border-sm-right color-border-muted">
              <Link
                href="#closed-issues"
                className="d-block color-fg-default no-underline Link--muted Link--inTextBlock"
              >
                <span className="d-block h4 color-fg-default">
                  <IssueClosedIcon className="color-fg-done" /> {overviewData.closedIssues.length}
                </span>
                <span className="color-fg-muted">Closed issues</span>
              </Link>
            </li>

            {/* New Issues */}
            <li className="pr-p-3 col-12 col-sm-6 col-md-3">
              <Link
                href="#opened-issues"
                className="d-block color-fg-default no-underline Link--muted Link--inTextBlock"
              >
                <span className="d-block h4 color-fg-default">
                  <IssueOpenedIcon className="color-fg-success" /> {overviewData.newIssues.length}
                </span>
                <span className="color-fg-muted">New issues</span>
              </Link>
            </li>
          </ul>
        </li>
      </ul>
    </div>
  )
}
